import React, { useState, useEffect, useRef } from "react";
import { RxCrossCircled } from "react-icons/rx";
import { FaMapMarkerAlt, FaSearch } from "react-icons/fa";

const MapLocationSelector = ({ isOpen, onClose, onSelectLocation }) => {
  const [currentLocation, setCurrentLocation] = useState(null);
  const [address, setAddress] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [searchQuery, setSearchQuery] = useState("");
  const [searchResults, setSearchResults] = useState([]);
  const [showSearchResults, setShowSearchResults] = useState(false);
  const [mapLoaded, setMapLoaded] = useState(false);
  const mapRef = useRef(null);
  const markerRef = useRef(null);
  const mapInstanceRef = useRef(null);
  const geocoderRef = useRef(null);

  // Check if Google Maps API is already loaded
  const isGoogleMapsLoaded = () => {
    return window.google && window.google.maps;
  };

  // Load Google Maps API if not already loaded
  const loadGoogleMapsScript = () => {
    if (isGoogleMapsLoaded()) {
      console.log("Google Maps API already loaded");
      setMapLoaded(true);
      return;
    }

    console.log("Loading Google Maps API script");
    const googleMapsApiKey = process.env.REACT_APP_GEOCODING_API_KEY;

    if (!googleMapsApiKey) {
      console.error("Google Maps API key is missing");
      setError(
        "Google Maps API key is not configured. Please check your environment variables."
      );
      return;
    }

    const script = document.createElement("script");
    script.src = `https://maps.googleapis.com/maps/api/js?key=${googleMapsApiKey}&libraries=places`;
    script.async = true;
    script.defer = true;
    script.onload = () => {
      console.log("Google Maps API script loaded");
      setMapLoaded(true);
    };
    script.onerror = () => {
      console.error("Failed to load Google Maps API script");
      setError(
        "Failed to load Google Maps. Please check your internet connection and try again."
      );
    };
    document.head.appendChild(script);
  };

  // Initialize map when component mounts and modal is open
  useEffect(() => {
    if (isOpen) {
      loadGoogleMapsScript();
    }
  }, [isOpen]);

  // Initialize map when API is loaded
  useEffect(() => {
    if (isOpen && mapLoaded) {
      initializeMap();
    }
  }, [isOpen, mapLoaded]);

  const initializeMap = () => {
    if (!mapRef.current) {
      console.error("Map container reference is not available");
      return;
    }

    setLoading(true);
    setError(null);

    console.log("Initializing map");

    // Try to get user's current location
    if (navigator.geolocation) {
      navigator.geolocation.getCurrentPosition(
        (position) => {
          const { latitude, longitude } = position.coords;
          console.log("Got user location:", latitude, longitude);
          setCurrentLocation({ lat: latitude, lng: longitude });
          createMap({ lat: latitude, lng: longitude });
          reverseGeocode({ lat: latitude, lng: longitude });
          setLoading(false);
        },
        (err) => {
          console.error("Error getting location:", err);
          setError(
            "Could not get your current location. Please search for a location."
          );
          // Default to a fallback location (e.g., city center)
          const defaultLocation = { lat: 28.6139, lng: 77.209 }; // Delhi, India
          setCurrentLocation(defaultLocation);
          createMap(defaultLocation);
          setLoading(false);
        },
        { enableHighAccuracy: true, timeout: 10000, maximumAge: 0 }
      );
    } else {
      setError("Geolocation is not supported by your browser");
      const defaultLocation = { lat: 28.6139, lng: 77.209 }; // Delhi, India
      setCurrentLocation(defaultLocation);
      createMap(defaultLocation);
      setLoading(false);
    }
  };

  const createMap = (location) => {
    if (!mapRef.current) {
      console.error("Map container ref is not available");
      return;
    }

    if (!window.google || !window.google.maps) {
      console.error("Google Maps API not loaded");
      setError(
        "Google Maps API failed to load. Please refresh the page and try again."
      );
      setLoading(false);
      return;
    }

    console.log("Creating map with location:", location);

    try {
      const mapOptions = {
        center: location,
        zoom: 15,
        mapTypeControl: false,
        fullscreenControl: false,
      };

      const map = new window.google.maps.Map(mapRef.current, mapOptions);
      mapInstanceRef.current = map;

      // Create a marker at the location
      const marker = new window.google.maps.Marker({
        position: location,
        map: map,
        draggable: true,
        animation: window.google.maps.Animation.DROP,
      });
      markerRef.current = marker;

      // Add event listener for drag end
      marker.addListener("dragend", () => {
        const newPosition = marker.getPosition();
        const newLocation = {
          lat: newPosition.lat(),
          lng: newPosition.lng(),
        };
        console.log("Marker dragged to:", newLocation);
        setCurrentLocation(newLocation);
        reverseGeocode(newLocation);
      });

      // Create geocoder instance
      geocoderRef.current = new window.google.maps.Geocoder();

      console.log("Map created successfully");
    } catch (error) {
      console.error("Error creating map:", error);
      setError("Failed to create map. Please try again.");
      setLoading(false);
    }
  };

  const reverseGeocode = (location) => {
    if (!geocoderRef.current) {
      console.error("Geocoder not initialized");
      return;
    }

    console.log("Reverse geocoding location:", location);

    geocoderRef.current.geocode({ location }, (results, status) => {
      if (status === "OK" && results[0]) {
        console.log("Geocoder results:", results[0].formatted_address);
        setAddress(results[0].formatted_address);
      } else {
        console.error("Geocoder failed:", status);
        setAddress("Address not found");
      }
    });
  };

  const handleSearch = () => {
    if (!searchQuery.trim() || !window.google) return;

    console.log("Searching for:", searchQuery);

    const service = new window.google.maps.places.PlacesService(
      document.createElement("div")
    );

    const request = {
      query: searchQuery,
      fields: ["name", "geometry", "formatted_address"],
    };

    service.textSearch(request, (results, status) => {
      if (status === window.google.maps.places.PlacesServiceStatus.OK) {
        console.log("Search results:", results);
        setSearchResults(results);
        setShowSearchResults(true);
      } else {
        console.error("Places search failed:", status);
        setSearchResults([]);
        setShowSearchResults(false);
      }
    });
  };

  const handleSelectSearchResult = (result) => {
    if (!result.geometry || !result.geometry.location) return;

    const location = {
      lat: result.geometry.location.lat(),
      lng: result.geometry.location.lng(),
    };

    console.log("Selected search result:", result.formatted_address, location);

    setCurrentLocation(location);
    setAddress(result.formatted_address);
    setShowSearchResults(false);
    setSearchQuery("");

    // Update map and marker
    if (mapInstanceRef.current && markerRef.current) {
      mapInstanceRef.current.setCenter(location);
      markerRef.current.setPosition(location);
    }
  };

  const handleConfirmLocation = () => {
    if (currentLocation && address) {
      console.log("Confirming location:", {
        location: currentLocation,
        address: address,
      });
      onSelectLocation({
        location: currentLocation,
        address: address,
      });
      onClose();
    }
  };

  const handleKeyPress = (e) => {
    if (e.key === "Enter") {
      handleSearch();
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-2xl w-full max-w-2xl mx-auto max-h-[90vh] overflow-hidden flex flex-col">
        <div className="p-4 border-b border-gray-200">
          <div className="flex justify-between items-center">
            <h2 className="text-xl font-bold">Choose Location on Map</h2>
            <RxCrossCircled
              className="cursor-pointer text-red-600 hover:text-red-700"
              size={24}
              onClick={onClose}
            />
          </div>

          <div className="mt-3 relative">
            <div className="flex items-center border border-gray-300 rounded-lg overflow-hidden">
              <input
                type="text"
                placeholder="Search for a location..."
                className="flex-1 p-2 outline-none"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                onKeyPress={handleKeyPress}
              />
              <button
                className="bg-red-600 text-white p-2"
                onClick={handleSearch}
              >
                <FaSearch />
              </button>
            </div>

            {showSearchResults && searchResults.length > 0 && (
              <div className="absolute w-full mt-1 bg-white border border-gray-300 rounded-md shadow-lg z-10 max-h-40 overflow-y-auto">
                {searchResults.map((result, index) => (
                  <div
                    key={index}
                    className="p-2 hover:bg-gray-100 cursor-pointer border-b last:border-b-0"
                    onClick={() => handleSelectSearchResult(result)}
                  >
                    <p className="text-sm">
                      {result.formatted_address || result.name}
                    </p>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>

        <div className="flex-1 min-h-[400px] relative">
          {loading && (
            <div className="absolute inset-0 flex items-center justify-center bg-white bg-opacity-70 z-10">
              <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-red-600"></div>
            </div>
          )}

          {error && (
            <div className="absolute top-0 left-0 right-0 bg-red-100 text-red-700 p-2 text-center z-10">
              {error}
            </div>
          )}

          <div
            ref={mapRef}
            className="w-full h-full bg-gray-100"
            style={{ minHeight: "400px" }} // Ensure the map has a fixed height
          ></div>

          <div className="absolute bottom-0 left-0 right-0 bg-white p-4 border-t border-gray-200">
            <div className="flex items-start gap-2 mb-3">
              <FaMapMarkerAlt className="text-red-600 mt-1" />
              <div>
                <p className="font-semibold">Selected Location</p>
                <p className="text-sm text-gray-700">
                  {address || "No address selected"}
                </p>
              </div>
            </div>

            <button
              className="w-full bg-red-600 text-white py-2 rounded-lg font-medium hover:bg-red-700 transition-colors disabled:bg-gray-400 disabled:cursor-not-allowed"
              onClick={handleConfirmLocation}
              disabled={!currentLocation || !address}
            >
              Confirm Location
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default MapLocationSelector;
